// src/hooks/useHiringRequisitions.js
// Firestore hooks for Admin Hiring Requisitions

import { useState, useEffect } from "react";
import {
  collection, doc, query, where, orderBy,
  onSnapshot, addDoc, updateDoc, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { createJobOpening, logRecruitmentActivity } from "./useRecruitment";

// ── All Requisitions (Admin/HR view) ──────────────────────────────────────
export function useHiringRequisitions(statusFilter = null) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = statusFilter
      ? query(
          collection(db, "hiringRequisitions"),
          where("status", "==", statusFilter),
          orderBy("createdAt", "desc")
        )
      : query(collection(db, "hiringRequisitions"), orderBy("createdAt", "desc"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (e) => { console.error("[useHiringRequisitions]", e); setLoading(false); }
    );
    return unsub;
  }, [statusFilter]);

  return { data, loading };
}

// ── Create a new Hiring Requisition (Admin) ───────────────────────────────
export async function createHiringRequisition(data) {
  const ref = await addDoc(collection(db, "hiringRequisitions"), {
    ...data,
    status: data.status || "Submitted",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  try {
    await addDoc(collection(db, "notifications"), {
      recipientRole: "hr",
      title: "New Hiring Requisition Submitted",
      message: `Admin has submitted a hiring requisition for "${data.title}" in the ${data.department} department. Please review and act.`,
      link: "/hr/recruitment",
      read: false,
      createdAt: serverTimestamp(),
    });
  } catch (_) {}

  return ref.id;
}

// ── Update Requisition ────────────────────────────────────────────────────
export async function updateHiringRequisition(id, updates) {
  await updateDoc(doc(db, "hiringRequisitions", id), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

// ── HR: Approve Requisition ───────────────────────────────────────────────
export async function approveHiringRequisition(id, { hrId, hrName, hrRemarks = "" } = {}) {
  await updateDoc(doc(db, "hiringRequisitions", id), {
    status: "Approved",
    hrReviewedById: hrId || "",
    hrReviewedByName: hrName || "HR",
    hrRemarks,
    hrReviewedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  try {
    await addDoc(collection(db, "notifications"), {
      recipientRole: "admin",
      title: "Hiring Requisition Approved",
      message: `HR has approved your hiring requisition. A job opening has been or will be created shortly.`,
      link: "/admin/hiring-requisitions",
      read: false,
      createdAt: serverTimestamp(),
    });
  } catch (_) {}
}

// ── HR: Reject Requisition ────────────────────────────────────────────────
export async function rejectHiringRequisition(id, { hrId, hrName, hrRemarks = "" } = {}) {
  await updateDoc(doc(db, "hiringRequisitions", id), {
    status: "Rejected",
    hrReviewedById: hrId || "",
    hrReviewedByName: hrName || "HR",
    hrRemarks,
    hrReviewedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  try {
    await addDoc(collection(db, "notifications"), {
      recipientRole: "admin",
      title: "Hiring Requisition Rejected",
      message: `HR has rejected your hiring requisition. Reason: ${hrRemarks || "No reason provided."}`,
      link: "/admin/hiring-requisitions",
      read: false,
      createdAt: serverTimestamp(),
    });
  } catch (_) {}
}

// ── HR: Convert Approved Requisition to Job Opening ───────────────────────
export async function convertRequisitionToJob(requisition, hrName) {
  const jobId = await createJobOpening({
    title: requisition.title,
    department: requisition.department,
    openings: requisition.openings || 1,
    employmentType: requisition.employmentType || "Full-Time",
    experience: requisition.experience || "",
    skills: requisition.skills || [],
    salaryRange: requisition.salaryRange || "",
    priority: requisition.priority || "Medium",
    description: requisition.justification || "",
    status: "Open",
    sourceRequisitionId: requisition.id,
    createdByName: hrName,
  });

  await updateDoc(doc(db, "hiringRequisitions", requisition.id), {
    linkedJobOpeningId: jobId,
    status: "Converted",
    updatedAt: serverTimestamp(),
  });

  try {
    await addDoc(collection(db, "notifications"), {
      recipientRole: "admin",
      title: "Job Opening Created from Requisition",
      message: `HR has converted your hiring requisition "${requisition.title}" into an active job opening.`,
      link: "/admin/hiring-requisitions",
      read: false,
      createdAt: serverTimestamp(),
    });
  } catch (_) {}

  await logRecruitmentActivity({
    actorId: "",
    actorName: hrName,
    actorRole: "hr",
    action: "REQUISITION_CONVERTED",
    entityType: "hiringRequisition",
    entityId: requisition.id,
    entityName: requisition.title,
    newValue: `Job Opening created: ${jobId}`,
  });

  return jobId;
}
